import { useInfiniteQuery, type UseInfiniteQueryOptions } from 'react-query'
import { type Response } from 'request-dot-js'

import { AlmanacFilesClient, type RansackQ } from 'api/clients/AlmanacFilesClient'
import { reportError } from 'utils/error'
import { getNextPageParam } from 'utils/getNextPageParam'

import { filesKeys, type FilesInfiniteParams } from './filesKeys'
import { type AlmanacFilesIndexResponse } from './types'
import { useCurrentWorkspaceIdWithProject } from 'hooks/useCurrentWorkspaceIdWithProject'

type QueryFnData = Response<AlmanacFilesIndexResponse>

interface UseFilesParams extends FilesInfiniteParams {
  items?: number
  q?: RansackQ
  partialCalendar?: boolean
}

type UseFilesOptions = UseInfiniteQueryOptions<QueryFnData>

export interface FilesQueryData {
  pages: Array<QueryFnData>
  pageParams: unknown[]
}

export const useFiles = ({ items = 25, q, partialCalendar }: UseFilesParams = {}, options?: UseFilesOptions) => {
  const workspaceId = useCurrentWorkspaceIdWithProject()

  const userEnabled = options?.enabled ?? true
  const userOnError = options?.onError
  const mergedOptions: UseInfiniteQueryOptions<QueryFnData> = {
    ...(options ?? {}),
    getNextPageParam,
    enabled: Boolean(workspaceId) && userEnabled,
    onError: (error) => {
      reportError(error, { severity: 'error' })
      userOnError?.(error)
    },
  }

  return useInfiniteQuery<QueryFnData>(
    options?.queryKey || filesKeys.infinite(workspaceId, { items, q, partialCalendar }),
    ({ pageParam = 1 }) => {
      if (!workspaceId) {
        throw new Error('A workspace must be selected before fetching files.')
      }

      const page = Math.max(1, Number(pageParam) || 1)

      return AlmanacFilesClient.index(workspaceId, {
        page,
        items,
        q,
        partial_calendar: partialCalendar,
      })
    },
    mergedOptions,
  )
}
